import moment from 'moment'
import { useAccountingStore } from '@/stores/accounting.store'
import { useGarageStore } from '@/stores/garage.store'
import { useOrderStore } from '@/stores/order.store'
import { usePortalStore } from '@/stores/portal.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { getPropertyName } from '@/lib/utils'
import { useSaleStore } from '@/stores/sale.store'

export const useGetOrFetchAsync = async(entities, getFunctionAsync, force = false) => {
  if (!entities.value?.length || force) {
    const res = await getFunctionAsync()
    if (res.data) {
      entities.value = res.data
    }
  }
  return entities.value
}

export const useCheckLastUpdate = async(stores, promises, functionNameFn = null, force = false) => {
  const webSiteStore = useWebSiteStore()

  if (!Array.isArray(stores)) {
    stores = [stores]
  }

  if ((!webSiteStore.lastUpdates || !webSiteStore.lastUpdatesTimestamp || moment().diff(webSiteStore.lastUpdatesTimestamp, 'seconds') > window.config.CACHE_TIMEOUT) || force) {
    await webSiteStore.fetchLastUpdateAsync(true)
    webSiteStore.lastUpdatesTimestamp = Date.now()
  }

  const storeIds = stores.map(store => store.$id)

  webSiteStore.lastUpdates.filter(x => storeIds.some(id => x.model.startsWith(id))).forEach(x => {
    const modelName = x.model.split('.').pop()
    const lastLocalUpdate = webSiteStore.lastUpdateDictionary[x.model]
    if (!lastLocalUpdate || lastLocalUpdate !== x.updated_at) {
      const functionName = (functionNameFn && functionNameFn(modelName)) || `fetch${modelName}sAsync`
      stores.forEach(store => {
        if (x.model.startsWith(store.$id) && store[functionName]) {
          promises.push(
            store[functionName](true, { $notop: true }).then(() => {
              webSiteStore.lastUpdateDictionary[x.model] = x.updated_at
            })
          )
        }
      })
    }
  })

  return promises
}

export const refreshAllStoresAsync = async() => {
  const portalStore = usePortalStore()
  const accountingStore = useAccountingStore()
  const registryStore = useRegistryStore()
  const orderStore = useOrderStore()
  const garageStore = useGarageStore()
  const webSiteStore = useWebSiteStore()
  const saleStore = useSaleStore()

  const promises = []

  const storesToUpdate = [portalStore, accountingStore, registryStore, orderStore, garageStore, webSiteStore, saleStore]

  await useCheckLastUpdate(
    storesToUpdate,
    promises,
    (modelName) => {
      switch (modelName) {
        case 'Branch': return getPropertyName(registryStore, x => x.fetchBranchesAsync)
        case 'CompanyData': return getPropertyName(registryStore, x => x.fetchCompanyDataAsync)
        case 'DiscountCategory': return getPropertyName(registryStore, x => x.fetchDiscountCategoriesAsync)
        case 'Country': return getPropertyName(webSiteStore, x => x.fetchCountriesAsync)
        case 'Currency': return getPropertyName(webSiteStore, x => x.fetchCurrenciesAsync)
        case 'ProductFamily': return getPropertyName(registryStore, x => x.fetchProductFamiliesAsync)
        case 'IdentityAuthority': return getPropertyName(registryStore, x => x.fetchIdentityDocumentAuthoritiesAsync)
        case 'UnitOfMeasure': return getPropertyName(webSiteStore, x => x.fetchUnitsOfMeasureAsync)
        case 'PurchaseFamily': return getPropertyName(portalStore, x => x.fetchPurchaseFamiliesAsync)
        case 'ProductTypology': return getPropertyName(portalStore, x => x.fetchProductTypologiesAsync)
        case 'ProductManufacturerFamily': return getPropertyName(portalStore, x => x.fetchProductManufacturerFamiliesAsync)
        case 'ElectronicInvoiceProgressStatus': return getPropertyName(portalStore, x => x.fetchElectronicInvoiceProgressStatusesAsync)
        case 'DocumentProgressStatus': return getPropertyName(garageStore, x => x.fetchDocumentProgressStatusesAsync)
        case 'DocumentSeries': return getPropertyName(registryStore, x => x.fetchDocumentSeriesAsync)
        default: return null
      }
    },
    true
  )

  await Promise.all(promises)
}
